<template>
  <div class="ai-chat-body d-flex flex-column flex-grow-1 min-h-0 overflow-hidden">
    <template v-if="!hideHeader">
      <div class="ai-header d-flex align-center justify-space-between px-4">
        <div class="d-flex align-center ga-3">
          <div class="ai-header__icon-wrap d-flex align-center justify-center flex-shrink-0">
            <v-img
              :src="assistantAvatarSrc"
              alt=""
              class="ai-brand-logo"
            />
          </div>
          <div>
            <p class="text-subtitle-2 font-weight-bold mb-0">{{ t('ai-assistant.title') }}</p>
            <p class="text-caption text-disabled mb-0">{{ t('ai-assistant.powered-by') }}</p>
          </div>
        </div>
        <div class="d-flex align-center">
          <v-btn
            variant="text"
            size="default"
            :aria-label="t('ai-assistant.clear-chat')"
            icon="mdi-delete-outline"
            :disabled="messages.length === 0"
            @click="aiStore.clearMessages()"
          />
          <v-btn
            v-if="!hideCloseButton"
            variant="text"
            size="default"
            :aria-label="t('ai-assistant.close')"
            icon="mdi-close"
            @click="isOpen = false"
          />
        </div>
      </div>
      <v-divider />
    </template>

    <ScrapeStatusBar
      v-if="showScrapeProgress"
      :progress="scrapeProgressPanelView"
      @dismiss="aiStore.dismissScrapeProgressPanel()"
    />

    <div
      ref="messagesContainer"
      class="ai-messages px-4 pt-4 pb-2"
      :class="{ 'ai-messages--scrollable': compact }"
    >
      <div
        v-if="messages.length === 0"
        class="ai-empty d-flex flex-column align-center justify-center"
      >
        <div class="ai-empty__glow mb-4">
          <v-img
            :src="assistantAvatarSrc"
            alt=""
            class="ai-brand-logo"
          />
        </div>
        <p class="text-subtitle-2 font-weight-medium mb-1">
          {{ t('ai-assistant.empty-state.title') }}
        </p>
        <p class="text-caption text-disabled text-center px-6 mb-4">
          {{ t('ai-assistant.empty-state.subtitle') }}
        </p>
        <div class="ai-suggestions d-flex flex-wrap justify-center ga-2 px-2">
          <v-chip
            v-for="suggestion in suggestions"
            :key="suggestion.key"
            size="small"
            variant="tonal"
            color="primary"
            class="ai-suggestion-chip"
            @click="sendSuggestion(suggestion.text)"
          >
            {{ suggestion.text }}
          </v-chip>
        </div>
      </div>

      <template v-else>
        <template
          v-for="message in messages"
          :key="message.id"
        >
          <div
            v-show="isMessageVisible(message)"
            class="ai-message-row d-flex align-start mb-3"
            :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <v-avatar
              v-if="message.role === 'assistant'"
              size="28"
              rounded="lg"
              class="ai-avatar mr-2 flex-shrink-0 mt-1"
            >
              <v-img
                :src="assistantAvatarSrc"
                alt=""
                class="ai-brand-logo"
              />
            </v-avatar>

            <div
              class="ai-bubble pa-3"
              :class="message.role === 'user' ? 'ai-bubble--user' : 'ai-bubble--assistant'"
            >
              <span
                v-if="message.role === 'assistant'"
                class="text-body-1 ai-bubble-content"
                >{{ message.content }}</span
              ><span
                v-else
                class="text-body-1"
                >{{ message.content }}</span
              ><span
                v-if="message.streaming && message.content.trim()"
                class="ai-cursor"
                aria-hidden="true"
              />
            </div>
          </div>

          <div
            v-for="widget in widgetsAfterMessage(message.id)"
            :key="widget.id"
            class="ai-message-row d-flex align-start mb-3 justify-start"
          >
            <v-avatar
              size="28"
              rounded="lg"
              class="ai-avatar mr-2 flex-shrink-0 mt-1"
            >
              <v-img
                :src="assistantAvatarSrc"
                alt=""
                class="ai-brand-logo"
              />
            </v-avatar>
            <div class="ai-bubble ai-bubble--assistant pa-3 ai-bubble--widget">
              <ChatInlineWidgetCompleted :widget="widget" />
            </div>
          </div>
        </template>

        <div
          v-if="showInlineWidget"
          class="ai-message-row d-flex align-start mb-3 justify-start"
        >
          <v-avatar
            size="28"
            rounded="lg"
            class="ai-avatar mr-2 flex-shrink-0 mt-1"
          >
            <v-img
              :src="assistantAvatarSrc"
              alt=""
              class="ai-brand-logo"
            />
          </v-avatar>
          <div class="ai-bubble ai-bubble--assistant pa-3 ai-bubble--widget">
            <div
              v-if="activeWidgetTitle || activeWidgetDescription"
              class="ai-widget-header mb-3"
            >
              <p
                v-if="activeWidgetTitle"
                class="text-body-2 font-weight-medium mb-1"
              >
                {{ activeWidgetTitle }}
              </p>
              <p
                v-if="activeWidgetDescription"
                class="text-caption text-medium-emphasis mb-0"
              >
                {{ activeWidgetDescription }}
              </p>
            </div>
            <ChatImageSelection
              v-if="activeWidgetType === 'image_selection'"
              :candidates="imageCandidates"
              :form-key="widgetFormKey"
              :allow-upload="widgetAllowUpload"
              :company-id="companyId"
              :busy="widgetBusy"
              :title="pendingToolAction?.title"
              :description="pendingToolAction?.description"
              @select="onImageSelect"
              @upload="onImageUpload"
            />
            <ChatColorPalette
              v-else-if="activeWidgetType === 'color_palette'"
              :palette="colorPalette"
              :form-key="widgetFormKey"
              :allow-custom="widgetAllowCustom"
              :busy="widgetBusy"
              @select="onColorSelect"
              @none-of-above="onColorNone"
            />
            <ChatColorPicker
              v-else-if="activeWidgetType === 'color_picker'"
              :form-key="widgetFormKey"
              :busy="widgetBusy"
              @apply="onColorSelect"
            />
            <ChatWidgetIntegration
              v-else-if="activeWidgetType === 'widget_integration'"
              :form-key="widgetFormKey"
              :busy="widgetBusy"
              :initial-value="widgetIntegrationInitialValue"
              @confirm="onWidgetIntegrationConfirm"
            />
            <ChatScrapePersonPick
              v-else-if="activeWidgetType === 'scrape_person_pick'"
              :person-candidates="scrapeReviewPersons"
              :busy="widgetBusy"
              @confirm="onScrapePersonConfirm"
            />
            <ChatConnectionCard
              v-else-if="activeWidgetType === 'connection_card'"
              :crm-providers="connectionCrmProviders"
              :social-providers="connectionSocialProviders"
              :busy="widgetBusy"
              @start="onConnectionStart"
              @connected="onConnectionConnected"
              @failed="onConnectionFailed"
              @finish="onConnectionFinish"
            />
            <ChatEmailFunnel
              v-else-if="activeWidgetType === 'email_funnel_setup'"
              :busy="widgetBusy"
              @confirm="onEmailFunnelConfirm"
            />
            <ChatAcquisition
              v-else-if="activeWidgetType === 'acquisition_setup'"
              :busy="widgetBusy"
              @confirm="onAcquisitionConfirm"
              @skip="onAcquisitionSkip"
            />
            <ChatLegalConsents
              v-else-if="activeWidgetType === 'legal_consents'"
              :company-id="companyId"
              :busy="widgetBusy"
              :license-already-accepted="licenseAlreadyAccepted"
              :is-us-onboarding="Boolean(uiContext?.isUsOnboarding)"
              @confirm="onLegalConsentsConfirm"
            />
            <ChatBillingContinue
              v-else-if="activeWidgetType === 'billing_continue'"
              :description="billingContinueDescription"
              :busy="widgetBusy"
              @confirm="onBillingContinue"
            />
          </div>
        </div>

        <div
          v-if="showAssistantPendingIndicator"
          class="ai-message-row d-flex align-start mb-3 justify-start"
        >
          <v-avatar
            size="28"
            rounded="lg"
            class="ai-avatar mr-2 flex-shrink-0 mt-1"
          >
            <v-img
              :src="assistantAvatarSrc"
              alt=""
              class="ai-brand-logo"
            />
          </v-avatar>
          <div class="ai-bubble ai-bubble--assistant pa-3 d-flex align-center ga-1">
            <span class="ai-dot" />
            <span class="ai-dot ai-dot--2" />
            <span class="ai-dot ai-dot--3" />
          </div>
        </div>
      </template>
    </div>

    <AiActionConfirmCard />

    <AiAssistantUnavailable v-if="assistantUnavailable" />

    <v-divider />
    <div
      v-if="!assistantUnavailable"
      class="ai-input-area px-3 pt-3 pb-3"
      :class="{ 'ai-input-area--compact': compact }"
    >
      <div class="ai-input-wrap">
        <v-textarea
          ref="chatInputRef"
          v-model="inputText"
          variant="outlined"
          density="compact"
          :placeholder="t('ai-assistant.input.placeholder')"
          :rows="compact ? 2 : 3"
          hide-details
          rounded="lg"
          :disabled="isLoading || !!pendingAction"
          class="ai-textarea"
          :class="{ 'ai-textarea--compact': compact }"
          @keydown="handleKeydown"
        />
        <v-btn
          icon
          variant="flat"
          size="32"
          color="primary"
          :disabled="!inputText.trim() || isLoading || !!pendingAction"
          class="ai-send-btn-inner"
          :aria-label="t('ai-assistant.input.send-aria-label')"
          @click="sendMessage"
        >
          <v-icon size="18">mdi-send</v-icon>
        </v-btn>
      </div>
      <p
        v-if="!compact"
        class="text-caption text-disabled text-center mt-2 mb-0"
      >
        {{ t('ai-assistant.input.hint') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import logoIcon from '@/assets/img/logo-icon.png'
import ChatAcquisition from '@/components/ai/onboarding/ChatAcquisition.vue'
import ChatBillingContinue from '@/components/ai/onboarding/ChatBillingContinue.vue'
import ChatColorPalette from '@/components/ai/onboarding/ChatColorPalette.vue'
import ChatColorPicker from '@/components/ai/onboarding/ChatColorPicker.vue'
import ChatConnectionCard from '@/components/ai/onboarding/ChatConnectionCard.vue'
import ChatEmailFunnel from '@/components/ai/onboarding/ChatEmailFunnel.vue'
import ChatImageSelection from '@/components/ai/onboarding/ChatImageSelection.vue'
import ChatInlineWidgetCompleted from '@/components/ai/onboarding/ChatInlineWidgetCompleted.vue'
import ChatLegalConsents from '@/components/ai/onboarding/ChatLegalConsents.vue'
import ChatScrapePersonPick from '@/components/ai/onboarding/ChatScrapePersonPick.vue'
import ChatWidgetIntegration from '@/components/ai/onboarding/ChatWidgetIntegration.vue'
import ScrapeStatusBar from '@/components/ai/onboarding/ScrapeStatusBar.vue'
import AiActionConfirmCard from '@/components/AiActionConfirmCard.vue'
import AiAssistantUnavailable from '@/components/AiAssistantUnavailable.vue'
import { useSocialMedia } from '@/composables/useSocialMedia'
import { applyChatLegalConsents } from '@/lib/ai/applyChatLegalConsents'
import { MAKLER_INLINE_WIDGET_TYPES } from '@/lib/ai/assistantApi'
import {
  buildConnectionToolData,
  type ConnectionAction,
  type ConnectionType,
} from '@/lib/ai/connectionTool'
import { hasDisplayableChatContent } from '@/lib/ai/onboardingChat'
import { restoreChatInputFocus } from '@/lib/ai/restoreChatInputFocus'
import bus, { eventNames } from '@/lib/eventBus'
import { SocialMedia } from '@/modules/contentCreator/creator/enums/SocialMedia'
import {
  clearPendingSocialConnect,
  readPendingSocialConnect,
  writePendingSocialConnect,
} from '@/modules/onboarding/lib/pendingSocialConnect'
import { useAiAssistantStore } from '@/stores/aiAssistant'
import type { CompletedInlineWidget, Message } from '@/stores/aiAssistant/types'

const props = withDefaults(
  defineProps<{
    hideCloseButton?: boolean
    /** Hides the assistant title bar and clear-chat control (embedded onboarding column). */
    hideHeader?: boolean
    /** When true, hydrate session and run `contextLoader` on mount (embedded onboarding column). */
    loadContextOnMount?: boolean
    contextLoader?: () => string | Promise<string>
    /** Tighter composer for embedded onboarding (single-column chat). */
    compact?: boolean
  }>(),
  {
    hideCloseButton: false,
    hideHeader: false,
    loadContextOnMount: false,
    contextLoader: undefined,
    compact: false,
  }
)

const { t } = useI18n()
const assistantAvatarSrc = logoIcon
const aiStore = useAiAssistantStore()
const {
  isOpen,
  messages,
  isLoading,
  isStreaming,
  inputText,
  pendingAction,
  pendingToolAction,
  localColorPickerFormKey,
  widgetBusy,
  uiContext,
  scrapeProgressPanelView,
  bootstrapIntroComplete,
  completedInlineWidgets,
  assistantRevealTyping,
  assistantRevealBusy,
  assistantRevealActive,
  assistantUnavailable,
} = storeToRefs(aiStore)

const { reconcilePendingFacebookConnect } = useSocialMedia()

const showScrapeProgress = computed(() => {
  if (uiContext.value?.enableWebsiteScrape === false) return false
  if (uiContext.value?.activeStepId !== 'enter-data') return false
  if (!bootstrapIntroComplete.value) return false
  return Boolean(scrapeProgressPanelView.value)
})

const isMessageVisible = (message: Message): boolean => hasDisplayableChatContent(message.content)

// Completed-widget cards ("Primary color selected") render inline, right after
// the message they were anchored to (the user's pick), so they stay in
// conversational order instead of piling up at the bottom of the transcript.
const completedWidgetsByAnchor = computed(() => {
  const map = new Map<string, CompletedInlineWidget[]>()
  for (const widget of completedInlineWidgets.value) {
    if (!widget.afterMessageId) continue
    const list = map.get(widget.afterMessageId) ?? []
    list.push(widget)
    map.set(widget.afterMessageId, list)
  }
  return map
})

const widgetsAfterMessage = (messageId: string): CompletedInlineWidget[] =>
  completedWidgetsByAnchor.value.get(messageId) ?? []

const hasRenderableInlineWidget = computed((): boolean => {
  const type = activeWidgetType.value
  if (!type || !MAKLER_INLINE_WIDGET_TYPES.has(type)) return false
  if (type === 'color_palette') {
    return colorPalette.value.length > 0 || widgetAllowCustom.value
  }
  if (type === 'image_selection') {
    return imageCandidates.value.length > 0 || widgetAllowUpload.value
  }
  if (type === 'scrape_person_pick') {
    return scrapeReviewPersons.value.length > 0
  }
  return true
})

const showAssistantPendingIndicator = computed(
  () =>
    widgetBusy.value ||
    assistantRevealTyping.value ||
    assistantRevealActive.value ||
    (isLoading.value && !isStreaming.value)
)

const activeWidgetType = computed(() => {
  if (localColorPickerFormKey.value) return 'color_picker'
  return pendingToolAction.value?.type ?? null
})

// Self-contained widget header: the title/description travel with the
// `pending_tool_action` payload, so every inline widget explains itself even
// when no accompanying chat message was emitted (or it landed out of order).
// `widget_integration` is excluded — it renders its own branded title card.
// widget_integration and image_selection render their own headers.
const WIDGETS_WITH_OWN_HEADER = new Set([
  'widget_integration',
  'image_selection',
  'legal_consents',
  'billing_continue',
])

// Field labels arrive lowercase (they read mid-sentence in chat prompts, e.g.
// "your primary brand color"); as a widget heading they should start capitalized.
const capitalizeFirst = (text: string): string =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text

const activeWidgetTitle = computed((): string => {
  if (activeWidgetType.value && WIDGETS_WITH_OWN_HEADER.has(activeWidgetType.value)) return ''
  const title = pendingToolAction.value?.title
  if (typeof title === 'string' && title.trim()) return capitalizeFirst(title.trim())
  if (localColorPickerFormKey.value) return t('onboarding.wizard.chat-widgets.color-picker-title')
  return ''
})

const activeWidgetDescription = computed((): string => {
  if (activeWidgetType.value && WIDGETS_WITH_OWN_HEADER.has(activeWidgetType.value)) return ''
  const description = pendingToolAction.value?.description
  if (typeof description === 'string' && description.trim()) return description.trim()
  if (localColorPickerFormKey.value) {
    return t('onboarding.wizard.chat-widgets.color-picker-description')
  }
  return ''
})

const showInlineWidget = computed(() => {
  // The connection card drives a multi-second OAuth the user completes in a popup/redirect.
  // Its child SocialMediaWrapper owns the connect promise + success watcher (per-instance
  // state) and shows "Connected" reactively from the refetched socialMediaConnections —
  // exactly like the connection manager. So keep it mounted through the transient busy/
  // reveal states of its own start ping and outcome report, which would otherwise tear it
  // down mid-OAuth (orphaning the report) or the instant the outcome posts (hiding the
  // "Connected" state before the user ever sees it). genaisis advancing the step — which
  // moves activeWidgetType off 'connection_card' — is what removes it.
  if (activeWidgetType.value === 'connection_card') return hasRenderableInlineWidget.value
  if (assistantRevealBusy.value || widgetBusy.value) return false
  return hasRenderableInlineWidget.value
})

const widgetData = computed(() => pendingToolAction.value?.data ?? {})

const billingContinueDescription = computed((): string => {
  const description = pendingToolAction.value?.description
  return typeof description === 'string' && description.trim() ? description.trim() : ''
})

const widgetFormKey = computed(() =>
  String(widgetData.value.form_key ?? localColorPickerFormKey.value ?? '')
)

const widgetAllowUpload = computed(() => widgetData.value.allow_upload !== false)

const widgetAllowCustom = computed(() => widgetData.value.allow_custom !== false)

const companyId = computed(() => {
  const id = uiContext.value?.currentValues?.companyId
  return typeof id === 'string' && id.trim() ? id.trim() : null
})

const imageCandidates = computed(() => {
  const raw = widgetData.value.candidates
  return Array.isArray(raw) ? raw : []
})

const colorPalette = computed(() => {
  const raw = widgetData.value.palette
  if (!Array.isArray(raw)) return []
  return raw.map((c) => String(c).trim()).filter(Boolean)
})

const widgetIntegrationInitialValue = computed(() => {
  const raw = widgetData.value.current_value
  if (raw === true || raw === false) return raw
  if (raw === 'true') return true
  if (raw === 'false') return false
  return null
})

const scrapeReviewPersons = computed(() => {
  const raw = widgetData.value.person_candidates
  return Array.isArray(raw) ? raw : []
})

const connectionType = computed<ConnectionType | ''>(() => {
  const raw = String(widgetData.value.type ?? widgetData.value.kind ?? '').toLowerCase()
  return raw === 'crm' || raw === 'social' ? raw : ''
})

const connectionCrmProviders = computed<string[]>(() => {
  const raw = widgetData.value.crm_providers
  return Array.isArray(raw) ? raw.map((p) => String(p)) : []
})

const connectionSocialProviders = computed<string[]>(() => {
  const raw = widgetData.value.social_providers
  return Array.isArray(raw) ? raw.map((p) => String(p)) : []
})

const applyConnectionTool = (
  payload: {
    type: ConnectionType
    action: ConnectionAction
    provider?: string
    connected?: string[]
  },
  options?: { keepalive?: boolean }
): Promise<boolean> =>
  aiStore.applyOnboardingWidgetTool('connection', buildConnectionToolData(payload), options)

const onConnectionStart = ({
  type,
  provider,
  connected,
}: {
  type: ConnectionType
  provider: string
  connected: string[]
}) => {
  // Facebook connects via the FB SDK, which on mobile/in-app webviews does a full-page
  // OAuth redirect that reloads the SPA and destroys the in-page code that would report
  // the outcome. Persist the intent NOW (synchronously, before the redirect fires) so
  // the next boot can finish and report the connect. See reconcilePendingFacebookConnect.
  const isFacebook = type === 'social' && provider === SocialMedia.FACEBOOK
  if (isFacebook) {
    writePendingSocialConnect(companyId.value, { provider, connected })
  }
  // The redirect can tear the page down the instant FB.login runs, right after this
  // fires. `keepalive` lets the `social_connect_requested` request complete in flight so
  // genaisis reliably records the social step — otherwise the recovery boot finds no
  // connection_card, the awaitingSocial guard fails, and the connect can't be resumed.
  return applyConnectionTool(
    {
      type,
      action: type === 'crm' ? 'crm_connect_requested' : 'social_connect_requested',
      provider,
      connected,
    },
    isFacebook ? { keepalive: true } : undefined
  )
}

const onConnectionConnected = async ({
  type,
  provider,
  connected,
}: {
  type: ConnectionType
  provider: string
  connected: string[]
}): Promise<boolean> => {
  const reported = await applyConnectionTool({
    type,
    action: type === 'crm' ? 'crm_connected' : 'social_provider_connected',
    provider,
    connected,
  })
  // Drop the redirect-recovery marker only once the report is confirmed persisted, and
  // only for Facebook — it's the sole provider that writes one. Clearing on any social
  // outcome would let a later Instagram/LinkedIn success wipe a Facebook marker that was
  // deliberately retained after a failed Facebook report. If the apply failed, keep it so
  // the next boot can retry instead of stranding a live connection genaisis never recorded.
  if (reported && type === 'social' && provider === SocialMedia.FACEBOOK) {
    clearPendingSocialConnect(companyId.value)
  }
  return reported
}

const onConnectionFailed = async ({
  type,
  provider,
  connected,
}: {
  type: ConnectionType
  provider: string
  connected: string[]
}): Promise<boolean> => {
  const reported = await applyConnectionTool({
    type,
    action: type === 'crm' ? 'crm_failed' : 'social_failed',
    provider,
    connected,
  })
  if (reported && type === 'social' && provider === SocialMedia.FACEBOOK) {
    clearPendingSocialConnect(companyId.value)
  }
  return reported
}

const onConnectionFinish = ({ type, connected }: { type: ConnectionType; connected: string[] }) => {
  const resolvedType = type || connectionType.value || 'social'
  if (resolvedType === 'crm') {
    void applyConnectionTool({
      type: 'crm',
      action: connected.length ? 'crm_connected' : 'crm_skipped',
      provider: connected[0] ?? '',
      connected,
    })
    return
  }
  void applyConnectionTool({
    type: 'social',
    action: 'social_finished',
    provider: '',
    connected,
  })
}

const onEmailFunnelConfirm = ({ enabled }: { enabled: boolean }) => {
  void aiStore.applyOnboardingWidgetTool('email_funnel_setup', { enabled })
}

// Prepare with contacts already in the app; the CRM-sync / manual-import variants
// are configured later. Skip declines the optional booster (contacts_source stays null).
const onAcquisitionConfirm = ({ contactsSource }: { contactsSource: string }) => {
  void aiStore.applyOnboardingWidgetTool('acquisition_setup', {
    enabled: true,
    contacts_source: contactsSource,
    needs_import: false,
    ready: true,
  })
}

const onAcquisitionSkip = () => {
  void aiStore.applyOnboardingWidgetTool('acquisition_setup', {
    enabled: false,
    contacts_source: null,
    needs_import: false,
    ready: true,
  })
}

const licenseAlreadyAccepted = computed(() =>
  Boolean(uiContext.value?.currentValues?.licenseAgreementAccepted)
)

const onLegalConsentsConfirm = (answers: {
  acceptAgb: boolean
  acceptPrivacy: boolean
  acceptGuideline: boolean
  authorizeSendOnBehalf: boolean
  contactViaEmail: boolean
  contactViaWhatsapp: boolean
  acceptConfirmData: boolean
}) => {
  // Assign `chatLegalConsents` only after the tool persists. A failed/false
  // result must not leave optimistic state that skips the review iubenda fallback.
  void applyChatLegalConsents(aiStore, answers)
}

const onBillingContinue = () => {
  void aiStore.applyOnboardingWidgetTool('billing_continue', {}).then((ok) => {
    if (ok) bus.$emit(eventNames.ONBOARDING_CONTINUE_TO_BILLING)
  })
}

const onImageSelect = ({ formKey, url }: { formKey: string; url: string }) => {
  void aiStore.applyOnboardingWidgetTool('image_selection', { form_key: formKey, url })
}

const onImageUpload = ({ formKey, file }: { formKey: string; file: File }) => {
  void aiStore.uploadOnboardingImageFromChat({ formKey, file })
}

const onColorSelect = ({ formKey, hex }: { formKey: string; hex: string }) => {
  void aiStore.applyOnboardingWidgetTool('color_selection', { form_key: formKey, hex })
}

const onColorNone = ({ formKey }: { formKey: string }) => {
  void aiStore.requestLocalColorPicker(formKey)
}

const onWidgetIntegrationConfirm = ({
  formKey,
  enabled,
  userMessage,
}: {
  formKey: string
  enabled: boolean
  userMessage: string
}) => {
  void aiStore.applyOnboardingWidgetTool('widget_integration', {
    form_key: formKey,
    enabled,
    user_message: userMessage,
  })
}

const onScrapePersonConfirm = ({
  candidate_id,
  first_name,
  last_name,
}: {
  candidate_id?: string | null
  first_name?: string
  last_name?: string
}) => {
  void aiStore.applyOnboardingWidgetTool('scrape_person_confirm', {
    candidate_id: candidate_id ?? undefined,
    selected_person_id: candidate_id ?? undefined,
    first_name: first_name ?? undefined,
    last_name: last_name ?? undefined,
  })
}

const suggestions = computed(() => [
  { key: 'what-can-you-do', text: t('ai-assistant.empty-state.suggestions.what-can-you-do') },
  { key: 'show-brand-colors', text: t('ai-assistant.empty-state.suggestions.show-brand-colors') },
  {
    key: 'update-company-name',
    text: t('ai-assistant.empty-state.suggestions.update-company-name'),
  },
  {
    key: 'navigate-publication',
    text: t('ai-assistant.empty-state.suggestions.navigate-publication'),
  },
])

const sendSuggestion = async (text: string): Promise<void> => {
  if (isLoading.value || !!pendingAction.value) return
  aiStore.inputText = text
  await aiStore.sendMessage()
  await focusChatInput()
}

const messagesContainer = ref<HTMLElement | null>(null)
const chatInputRef = ref<{ focus: () => void } | null>(null)

const focusChatInput = async (): Promise<void> => {
  await nextTick()
  chatInputRef.value?.focus?.()
  restoreChatInputFocus()
}

const scrollToBottom = async (): Promise<void> => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

const sendMessage = async (): Promise<void> => {
  await aiStore.sendMessage()
  await focusChatInput()
}

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendMessage()
  }
}

const runContextLoader = async (): Promise<void> => {
  if (!props.contextLoader) return
  const ctx = await props.contextLoader()
  aiStore.setContext(ctx)
  try {
    const parsed = JSON.parse(ctx)
    if (parsed && typeof parsed === 'object' && 'activeStepId' in parsed) {
      aiStore.setUiContext(parsed)
    }
  } catch {
    // plain string context
  }
}

// After an onboarding boot, finish any Facebook connect that a full-page OAuth redirect
// interrupted. Runs after the session is hydrated so we can confirm genaisis is still on
// the social step before reporting; otherwise the stale intent is just cleared.
const reconcileFacebookOAuthReturn = async (): Promise<void> => {
  const id = companyId.value
  if (!readPendingSocialConnect(id)) return
  // A failed hydration leaves the session state empty, so we can't tell whether
  // genaisis is still on the social step. Keep the marker in place so the next
  // boot can retry instead of dropping the pending connect on a transient error.
  if (assistantUnavailable.value) return
  const awaitingSocial =
    activeWidgetType.value === 'connection_card' && connectionType.value === 'social'
  if (!awaitingSocial) {
    // The social card isn't showing yet. This is either genuinely stale (genaisis
    // already resolved the step) or just a transient miss — genaisis hasn't re-emitted
    // the card on this boot, or its state lagged this exact tick. We can't tell the two
    // apart, so DON'T drop the marker on a single miss: keep it and let a later boot
    // retry. The 10-min TTL retires a truly stale marker on its own, and a repeat
    // report to genaisis is idempotent, so leaving it costs nothing.
    return
  }
  const outcome = await reconcilePendingFacebookConnect(id)
  if (!outcome) return
  // The report handler clears the marker only when the apply persists, so a failed
  // report leaves the marker in place for the next boot to retry.
  const report = outcome.status === 'connected' ? onConnectionConnected : onConnectionFailed
  await report({ type: 'social', provider: outcome.provider, connected: outcome.connected })
}

const hydrateAndPrepare = async (): Promise<void> => {
  await runContextLoader()
  // Clear any stale failure flag so a successful retry can resume normal marker
  // cleanup instead of being treated as another failed hydration.
  aiStore.clearAssistantUnavailable()
  await aiStore.hydrateOnboardingSession()
  await reconcileFacebookOAuthReturn()
  await scrollToBottom()
}

watch(messages, () => scrollToBottom(), { deep: true })
watch(assistantRevealTyping, () => scrollToBottom())
watch(completedInlineWidgets, () => scrollToBottom(), { deep: true })
watch(scrapeProgressPanelView, () => scrollToBottom())
watch(showAssistantPendingIndicator, () => scrollToBottom())

watch(isLoading, async (loading, wasLoading) => {
  if (wasLoading && !loading && !pendingAction.value) {
    await focusChatInput()
  }
})

watch(isOpen, async (val) => {
  if (val && !props.loadContextOnMount) {
    await hydrateAndPrepare()
  }
})

onMounted(async () => {
  if (props.loadContextOnMount) {
    await hydrateAndPrepare()
  }
})
</script>

<style scoped>
.ai-chat-body {
  flex: 1 1 0;
  min-height: 0;
  max-height: 100%;
  height: 100%;
}

.ai-messages {
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ai-messages::-webkit-scrollbar {
  display: none;
}

.ai-header {
  flex-shrink: 0;
  height: 60px;
}

.ai-header__icon-wrap {
  width: 40px;
  height: 40px;
  padding: 6px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.1);
}

.ai-brand-logo {
  flex: none;
}

.ai-brand-logo :deep(.v-img__img) {
  object-fit: contain;
}

.ai-header__icon-wrap .ai-brand-logo {
  width: 28px;
  height: 28px;
}

.ai-empty__glow .ai-brand-logo {
  width: 52px;
  height: 52px;
}

.ai-avatar .ai-brand-logo {
  width: 100%;
  height: 100%;
}

.ai-messages--scrollable {
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--v-theme-on-surface), 0.35) transparent;
  -ms-overflow-style: auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.ai-messages--scrollable::-webkit-scrollbar {
  width: 6px;
}

.ai-messages--scrollable::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.35);
  border-radius: 999px;
}

.ai-messages--scrollable::-webkit-scrollbar-track {
  background: transparent;
}

.ai-empty {
  min-height: 120px;
}

.ai-empty__glow {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-avatar {
  flex-shrink: 0;
}

.ai-bubble {
  max-width: 82%;
  border-radius: 16px;
  word-break: break-word;
  line-height: 1.55;
}

.ai-bubble--widget {
  max-width: min(100%, 420px);
  width: 100%;
}

.ai-bubble--user {
  background-color: rgb(var(--v-theme-primary));
  color: #fff;
  border-bottom-right-radius: 4px;
}

.ai-bubble--assistant {
  background: rgba(var(--v-theme-on-surface), 0.08);
  color: rgb(var(--v-theme-on-surface));
  border-bottom-left-radius: 4px;
}

.ai-cursor {
  display: inline-block;
  width: 2px;
  height: 0.9em;
  background: currentColor;
  vertical-align: middle;
  margin-left: 2px;
  border-radius: 1px;
  animation: cursor-blink 0.9s step-end infinite;
}

@keyframes cursor-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.ai-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  animation: dot-bounce 1.3s ease-in-out infinite;
}

.ai-dot--2 {
  animation-delay: 0.18s;
}

.ai-dot--3 {
  animation-delay: 0.36s;
}

@keyframes dot-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.35;
  }
  30% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

.ai-input-area {
  flex-shrink: 0;
  background: rgb(var(--v-theme-surface));
}

.ai-input-wrap {
  position: relative;
}

.ai-textarea :deep(.v-field) {
  border-radius: 12px;
}

.ai-textarea :deep(.v-field__input) {
  padding-bottom: 44px !important;
}

.ai-textarea--compact :deep(.v-field__input) {
  padding-bottom: 40px !important;
  min-height: 56px;
}

.ai-input-area--compact {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.ai-send-btn-inner {
  position: absolute;
  bottom: 8px;
  right: 8px;
  border-radius: 10px;
  transition: transform 0.1s ease;
}

.ai-send-btn-inner:not(:disabled):hover {
  transform: scale(1.06);
}

.ai-bubble-content {
  white-space: pre-line;
}

.ai-suggestions {
  width: 100%;
}

.ai-suggestion-chip {
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.ai-suggestion-chip:hover {
  opacity: 0.8;
}
</style>
